import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Package, Save, X, Plus, Search, Users, FileText, FolderOpen, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { createCatalogItem, addCustomerPrice } from '@/lib/api';
import { getProductTypeName } from '@/lib/constants';
import { toast } from 'sonner';

import { Screen, CommandBar } from '@/components/data';
import {
  DynamicFieldForm,
  usePlyChangeHandler,
  PlyChangeDialog,
} from '@/components/DynamicFieldRenderer';
import { useIsMobile } from '@/hooks/useIsMobile';
import PhoneCreateCatalogItem from '@/pages/Catalog/PhoneCreateCatalogItem';

const CreateCatalogItemPage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [productConfigs, setProductConfigs] = useState({});

  // Mode: 'choose' | 'general' | 'customer'
  const [mode, setMode] = useState('choose');

  // Core fields
  const [itemName, setItemName] = useState('');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [minStockThreshold, setMinStockThreshold] = useState('0');

  // Customer-specific (Path A)
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOrders, setCustomerOrders] = useState([]);
  const [selectedOrderItem, setSelectedOrderItem] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Job card field values (shared between both paths)
  const [jobCardFieldValues, setJobCardFieldValues] = useState({});
  const [jobCardFields, setJobCardFields] = useState([]);

  // Ply handler
  const { plyDialog, handlePlyChange, confirmPlyChange, cancelPlyChange } = usePlyChangeHandler(
    jobCardFieldValues,
    setJobCardFieldValues
  );

  // Fetch product types
  useEffect(() => {
    api.get('/product-types').then(res => {
      setProductTypes(res.data.product_types || []);
    }).catch(() => {});
  }, []);

  // Fetch job card fields when product type changes
  useEffect(() => {
    if (!selectedProductType) { setJobCardFields([]); return; }
    if (productConfigs[selectedProductType]) {
      setJobCardFields(productConfigs[selectedProductType]);
      return;
    }
    api.get(`/product-configs/${selectedProductType}/job-card-fields`).then(res => {
      const fields = res.data?.fields || [];
      setProductConfigs(prev => ({ ...prev, [selectedProductType]: fields }));
      setJobCardFields(fields);
    }).catch(() => setJobCardFields([]));
  }, [selectedProductType]);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get('/customers', { params: { search: customerSearch, limit: 50 } });
      setCustomers(res.data.customers || []);
    } catch {}
  }, [customerSearch]);

  useEffect(() => { if (mode === 'customer') fetchCustomers(); }, [fetchCustomers, mode]);

  // Fetch customer's past order items
  const loadCustomerOrders = async (customerId) => {
    setLoadingOrders(true);
    try {
      const res = await api.get('/orders', {
        params: { customer_id: customerId, limit: 100 }
      });
      const orders = res.data.orders || [];
      // Flatten line items from non-cancelled orders
      const items = [];
      for (const order of orders) {
        if (order.status === 'cancelled') continue;
        for (const li of (order.line_items || [])) {
          items.push({
            order_id: order.id,
            order_display_id: order.order_id,
            line_item_id: li.id,
            item_name: li.item_name || li.name || 'Unnamed',
            product_type: li.order_type,
            quantity: li.quantity,
            order_status: order.status,
            specifications: li.specifications || {},
          });
        }
      }
      setCustomerOrders(items);
    } catch { setCustomerOrders([]); }
    setLoadingOrders(false);
  };

  // Handle customer selection
  const handleCustomerSelect = (custId) => {
    setSelectedCustomerId(custId);
    setSelectedOrderItem(null);
    setJobCardFieldValues({});
    if (custId) loadCustomerOrders(custId);
  };

  // Handle order item selection (Path A)
  const handleSelectOrderItem = async (item) => {
    setSelectedOrderItem(item);
    setSelectedProductType(item.product_type);
    setItemName(item.item_name);

    // Fetch job card specs for this line item
    try {
      const res = await api.get(`/job-cards/by-order/${item.order_id}`);
      const jobCards = res.data.job_cards || [];
      const jc = jobCards.find(j => j.line_item_id === item.line_item_id);
      if (jc) {
        setJobCardFieldValues(jc.specifications || {});
      } else {
        setJobCardFieldValues(item.specifications || {});
      }
    } catch {
      setJobCardFieldValues(item.specifications || {});
    }
  };

  const submit = async () => {
    if (!itemName || !selectedProductType) {
      toast.error('Item name and product type are required');
      return;
    }

    try {
      setLoading(true);

      if (mode === 'customer' && selectedOrderItem) {
        // Path A: Create from order item
        const payload = {
          order_id: selectedOrderItem.order_id,
          line_item_id: selectedOrderItem.line_item_id,
          customer_id: selectedCustomerId,
          name: itemName,
        };
        const res = await api.post('/catalog/items/from-order-item', payload);
        if (res.data.warning) {
          toast.warning(res.data.warning);
        }
        toast.success('Catalog item created from order item');
        navigate('/catalog');
      } else {
        // Path B (General) or Path A without order item
        const payload = {
          name: itemName,
          product_type: selectedProductType,
          customer_id: mode === 'customer' ? selectedCustomerId : null,
          order_creation_fields: {},
          job_card_fields: jobCardFieldValues,
          min_stock_threshold: parseInt(minStockThreshold) || 0,
        };
        await createCatalogItem(payload);
        toast.success('Catalog item created');
        navigate('/catalog');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create catalog item');
    }
    setLoading(false);
  };

  const handleSubmit = (e) => { e.preventDefault(); submit(); };

  const setFieldValue = (fieldName, value) => setJobCardFieldValues(prev => ({ ...prev, [fieldName]: value }));

  const plyChangeDialog = (
    <PlyChangeDialog open={plyDialog.open} onConfirm={confirmPlyChange} onCancel={cancelPlyChange} oldPly={plyDialog.oldPly} newPly={plyDialog.newPly} />
  );

  // tiles, tappable rows and 48px controls below lg; the same state, the same payload
  if (isMobile) {
    return (
      <>
        <PhoneCreateCatalogItem
          mode={mode} onPickMode={setMode} onBack={() => navigate('/catalog')}
          itemName={itemName} setItemName={setItemName}
          productTypes={productTypes} selectedProductType={selectedProductType}
          onProductType={(v) => { setSelectedProductType(v); setJobCardFieldValues({}); }}
          minStockThreshold={minStockThreshold} setMinStockThreshold={setMinStockThreshold}
          customers={customers} onCustomerSearch={setCustomerSearch}
          selectedCustomerId={selectedCustomerId} onCustomerSelect={handleCustomerSelect}
          customerOrders={customerOrders} loadingOrders={loadingOrders}
          selectedOrderItem={selectedOrderItem} onSelectOrderItem={handleSelectOrderItem}
          jobCardFields={jobCardFields} jobCardFieldValues={jobCardFieldValues}
          onFieldChange={setFieldValue} onPlyChange={handlePlyChange}
          loading={loading} onSubmit={submit}
        />
        {plyChangeDialog}
      </>
    );
  }

  // Mode selection screen
  if (mode === 'choose') {
    return (
      <div className="mx-auto w-full max-w-2xl space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/catalog')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-heading text-xl font-bold text-slate-900">Add Catalog Item</h1>
        </div>

        <p className="text-sm text-slate-500">
          Choose how you want to add a new catalog item.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card
            className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-brand-blue"
            onClick={() => setMode('customer')}
          >
            <CardContent className="p-6 text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-800">For a Specific Customer</h3>
              <p className="text-xs text-slate-500">
                Select a customer, pick from their past orders, and auto-fill specs. Perfect for saving repeat products.
              </p>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-teal-500"
            onClick={() => setMode('general')}
          >
            <CardContent className="p-6 text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto">
                <Package className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-800">General</h3>
              <p className="text-xs text-slate-500">
                Create a general catalog item not tied to any customer. Fill in the product type and specs manually.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Screen className="mx-auto w-full max-w-5xl">
      <CommandBar
        title={mode === 'customer' ? 'Add catalog item — customer specific' : 'Add catalog item — general'}
        subtitle={mode === 'customer'
          ? 'Pick from what they have ordered before, then save it as a product'
          : 'Pick a product type and fill in the specifications'}
        onBack={() => setMode('choose')}
      />

      <form onSubmit={handleSubmit} className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        {/* Path A: Customer Selection */}
        {mode === 'customer' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                <Users className="h-3.5 w-3.5" /> Step 1: Select Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={e => setCustomerSearch(e.target.value)}
                  className="flex-1"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                {customers.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className={`text-left p-2 rounded border text-sm transition-colors ${
                      selectedCustomerId === c.id
                        ? 'border-brand-blue bg-blue-50 text-blue-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleCustomerSelect(c.id)}
                  >
                    <p className="font-medium truncate">{c.customer_name}</p>
                    <p className="text-[10px] text-slate-400 truncate">{c.firm_name}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Path A: Past Order Items */}
        {mode === 'customer' && selectedCustomerId && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                <FileText className="h-3.5 w-3.5" /> Step 2: Select Order Item (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingOrders ? (
                <p className="text-xs text-slate-400">Loading order history...</p>
              ) : customerOrders.length === 0 ? (
                <p className="text-xs text-slate-400">No past order items found. Fill in details manually below.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {customerOrders.map((item, idx) => (
                    <button
                      key={`${item.order_id}-${item.line_item_id}`}
                      type="button"
                      className={`w-full text-left p-2 rounded border text-sm flex items-center justify-between transition-colors ${
                        selectedOrderItem?.line_item_id === item.line_item_id
                          ? 'border-brand-blue bg-blue-50'
                          : 'border-slate-100 hover:border-slate-200'
                      }`}
                      onClick={() => handleSelectOrderItem(item)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{item.item_name}</p>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                          <span>{item.order_display_id}</span>
                          <Badge variant="outline" className="text-[9px]">{getProductTypeName(item.product_type, true)}</Badge>
                          <span>Qty: {item.quantity}</span>
                        </div>
                      </div>
                      {selectedOrderItem?.line_item_id === item.line_item_id && (
                        <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Basic Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
              <Package className="h-3.5 w-3.5" /> {mode === 'customer' && selectedCustomerId ? 'Step 3: Item Details' : 'Item Details'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Product Name <span className="text-red-400">*</span></Label>
                <Input
                  value={itemName}
                  onChange={e => setItemName(e.target.value)}
                  placeholder="e.g., Burger Box 6-inch"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Product Type <span className="text-red-400">*</span></Label>
                <Select value={selectedProductType} onValueChange={v => { setSelectedProductType(v); setJobCardFieldValues({}); }}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {productTypes.map(pt => (
                      <SelectItem key={pt.code} value={pt.code}>{pt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Min Stock Threshold</Label>
                <Input
                  type="number"
                  value={minStockThreshold}
                  onChange={e => setMinStockThreshold(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Job Card Fields (same form as job card) */}
        {selectedProductType && jobCardFields.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
                Specifications (Job Card Fields)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DynamicFieldForm
                fields={jobCardFields}
                values={jobCardFieldValues}
                onChange={setFieldValue}
                columns={2}
                showEmptyState={false}
                onPlyChange={handlePlyChange}
                orderType={selectedProductType}
              />
            </CardContent>
          </Card>
        )}

        {/* Submit */}
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/catalog')}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="bg-brand-blue hover:bg-brand-blue-dark text-white">
            <Save className="h-4 w-4 mr-1" />
            {loading ? 'Creating...' : 'Create Catalog Item'}
          </Button>
        </div>
      </form>

      {plyChangeDialog}
    </Screen>
  );
};

export default CreateCatalogItemPage;
